import { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  useMobileMenu,
  usePlatformStore,
  useMarketingComputed,
} from "@platform/store";
import { useHandleNavigationClick } from "@core/features/mobileMenu";
import {
  useFetchActions,
  getOrganizationsFromActions,
  ACTION_TYPE_SLUG,
  type Action,
} from "@core/features/actions";
import { MarketingNavigationItems } from "./MarketingNavigationItems";
import { marketingNavigationItemsData } from "./marketingNavigationItems.data";
// By its own path rather than the feature's: the news components draw
// themselves with this feature's sections, so reaching for its index here
// would close a circle between the two.
import { useHasPublishedNews } from "@platform/features/news/hooks/useHasPublishedNews";
import { useActiveSection } from "../../hooks/useActiveSection";

export function MarketingNavigationItemsContainer() {
  const user = usePlatformStore((s) => s.user);
  const isSupportPageDisabled = usePlatformStore(
    (s) => s.featureFlags.isSupportPageDisabled
  );
  const { showLoginCta } = useMarketingComputed();

  const { close: closeMobileMenu } = useMobileMenu();

  const { handleNavigationClick } = useHandleNavigationClick({ closeMobileMenu });

  const navigate = useNavigate();
  const { pathname, hash } = useLocation();
  const isHome = pathname === "/";
  // News is in the nav only once something is published, since the section it
  // names is on the page only then.
  const { hasPublishedNews } = useHasPublishedNews();
  const navigationItems = useMemo(
    () =>
      marketingNavigationItemsData.filter(
        (item) => hasPublishedNews || item.href !== "/#news"
      ),
    [hasPublishedNews]
  );
  const sectionIds = useMemo(
    () =>
      navigationItems
        .filter((item) => item.href.includes("#"))
        .map((item) => item.href.split("#")[1]),
    [navigationItems]
  );
  const { activeId, pin } = useActiveSection(sectionIds, isHome);

  // A section link puts its anchor in the address, so the place can be shared
  // and the back button returns to it, and it works from pages the section is
  // not on. The address changing is what scrolls; asking for the anchor the
  // address already has changes nothing, so that case scrolls by hand.
  const handleNavigationAnchorClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    targetId: string,
    // Which item stays marked on the way: the destination's own, unless it is
    // a section with no item of its own and so belongs to another's.
    pinId: string = targetId
  ) => {
    e.preventDefault();
    closeMobileMenu();
    pin(pinId);
    if (isHome && hash === `#${targetId}`) {
      document.getElementById(targetId)?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    navigate(`/#${targetId}`);
  };

  // On the homepage the item is the section being read. A post is part of
  // News, so on a post's page News is the item marked.
  const activeHref = isHome
    ? activeId && `/#${activeId}`
    : pathname.startsWith("/news/")
      ? "/#news"
      : null;

  // Fetch user actions to build dynamic menu items
  const { data: actionsData } = useFetchActions({
    user,
    isEnabled: !!user,
  });

  const menuItems = useMemo(() => {
    const items: { title: string; href: string }[] = [
      { title: "Dashboard", href: "/dashboard" },
    ];

    if (!actionsData?.actions) {
      const fallbackItems = [...items];
      fallbackItems.push({ title: "Settings", href: "/account" });
      fallbackItems.push({ title: "Logout", href: "/logout" });
      return fallbackItems;
    }

    const actions = actionsData.actions;

    // Program (if has schedule or circle_view action)
    const programAction = actions.find(
      (action: Action) =>
        action.action_type?.slug === ACTION_TYPE_SLUG.SCHEDULE ||
        action.action_type?.slug === ACTION_TYPE_SLUG.CIRCLE_VIEW
    );
    if (programAction?.program) {
      items.push({
        title: "Program",
        href: `/program/${programAction.program}`,
      });
    }

    // Profile (if has any actions with a program)
    const firstActionWithProgram = actions.find(
      (action: Action) => action.program
    );
    if (firstActionWithProgram?.program) {
      items.push({
        title: "Profile",
        href: `/program/${firstActionWithProgram.program}/profile`,
      });
    }

    // Invitations (if has send_invitation action)
    const invitationAction = actions.find(
      (action: Action) =>
        action.action_type?.slug === ACTION_TYPE_SLUG.SEND_INVITATION
    );
    if (invitationAction?.program) {
      items.push({
        title: "Invitations",
        href: `/program/${invitationAction.program}/invitations`,
      });
    }

    // Resources (if has resource actions)
    const resourceAction = actions.find(
      (action: Action) =>
        action.action_type?.slug === ACTION_TYPE_SLUG.WORKBOOK_DOWNLOAD ||
        action.action_type?.slug === ACTION_TYPE_SLUG.SOCIALMEDIAKIT_DOWNLOAD
    );
    if (resourceAction?.program) {
      items.push({
        title: "Resources",
        href: `/program/${resourceAction.program}/resources`,
      });
    }

    // Support (when not disabled, requires a program)
    if (!isSupportPageDisabled) {
      const firstProgramId = actions[0]?.program;
      if (firstProgramId) {
        items.push({ title: "Support", href: `/program/${firstProgramId}/support` });
      }
    }

    // Organization Admin (if has org admin actions)
    const organizations = getOrganizationsFromActions(actions);
    for (const org of organizations) {
      items.push({
        title: org.name ?? "Organization Admin",
        href: `/program/${org.programId}/organization/${org.id}/admin`,
      });
    }

    // Settings and Logout (always)
    items.push({ title: "Settings", href: "/account" });
    items.push({ title: "Logout", href: "/logout" });

    return items;
  }, [actionsData?.actions, isSupportPageDisabled]);

  return (
    <MarketingNavigationItems
      onNavigationClick={handleNavigationClick}
      onNavigationAnchorClick={handleNavigationAnchorClick}
      user={user}
      showLoginCta={showLoginCta}
      items={navigationItems}
      menuItems={menuItems}
      activeHref={activeHref}
      // The one button on every page of the site. Without this the people
      // it sends to the waitlist form look as if they came from nowhere.
    />
  );
}

export default MarketingNavigationItemsContainer;
